import {
  Video, Users, Laptop, Headphones, ShieldCheck, BrainCircuit, LineChart, BarChart3, Cloud,
  Check, ArrowRight, MapPin, Play, Star,
} from "lucide-react";
import { Reveal } from "../components/Reveal";

const CLASSROOM_IMG = "/images/classroom-1.jpg";
{/* Replace this src with the actual Google Maps embed link for the real address. */}
const MAP_EMBED_SRC = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d112012.52732020589!2d77.20922666835081!3d28.677933858063827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfb04c65ae22d%3A0x8a6838c5dc54792c!2sGlobal%20Institute%20of%20Cyber%20Security%20%26%20Ethical%20Hacking%20in%20Delhi!5e0!3m2!1sen!2sin!4v1791308479214!5m2!1sen!2sin";

const Teal = ({ children }) => <span className="glow-accent" style={{ color: "var(--accent)" }}>{children}</span>;

const SectionHead = ({ label, title, sub }) => (
  <Reveal className="text-center max-w-2xl mx-auto">
    <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase" style={{ color: "var(--accent)" }}>
      <span className="w-5 h-px" style={{ background: "var(--accent)" }} />
      {label}
      <span className="w-5 h-px" style={{ background: "var(--accent)" }} />
    </span>
    <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold uppercase text-white leading-[1.12]">{title}</h2>
    {sub && <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-body)" }}>{sub}</p>}
  </Reveal>
);

const stats = [
  { value: "10+", label: "Years of Experience" },
  { value: "1000+", label: "Students Trained" },
  { value: "50+", label: "Live Sessions Conducted" },
  { value: "200+", label: "Happy Student Reviews" },
];

export const StatsSection = () => (
  <section className="py-16 sm:py-20" style={{ background: "var(--bg-secondary)", borderTop: "1px solid var(--input-border)", borderBottom: "1px solid var(--input-border)" }} data-testid="stats-section">
    <div className="max-w-6xl mx-auto px-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 100}>
            <div className="track-card rounded-2xl p-6 text-center" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`stat-card-${i}`}>
              <p className="font-display glow-accent text-3xl sm:text-4xl font-semibold" style={{ color: "var(--accent)" }}>{s.value}</p>
              <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const whyItems = [
  { icon: Video, title: "Learn Live, Not From Recordings", text: "Real teachers, real time, your questions answered on the spot." },
  { icon: Users, title: "Learn With Other Students", text: "Join a group of students who help and support each other." },
  { icon: Laptop, title: "Practice What You Learn", text: "Hands-on practice, not just theory." },
  { icon: Headphones, title: "Help Even When You're Not There", text: "Missed a class? Every session is recorded, and support is always available online." },
];

export const WhySection = () => (
  <section className="py-24 sm:py-32" style={{ background: "var(--bg-primary)" }} data-testid="why-section">
    <div className="max-w-6xl mx-auto px-5">
      <SectionHead label="What Makes Us Different" title={<>Not Just Another <Teal>Online Course</Teal></>} />
      <div className="mt-14 grid gap-6 sm:grid-cols-2">
        {whyItems.map((item, i) => (
          <Reveal key={item.title} delay={i * 100}>
            <div className="track-card h-full rounded-2xl p-7 flex gap-5" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`why-card-${i}`}>
              <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(14, 217, 181, 0.08)", color: "var(--accent)" }}>
                <item.icon size={20} strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-white tracking-wide">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--text-body)" }}>{item.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-3c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-3.9 3C3.6 21.4 7.4 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3l-3.9-3C.4 8.3 0 10.1 0 12s.4 3.7 1.3 5.3l3.9-3z" />
    <path fill="#EA4335" d="M12 4.7c2.3 0 3.9 1 4.8 1.9l3.5-3.5C18.1 1.1 15.2 0 12 0 7.4 0 3.6 2.6 1.3 6.7l3.9 3c1-2.9 3.7-5 6.8-5z" />
  </svg>
);

const Stars = () => (
  <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={14} style={{ color: "var(--star)" }} fill="var(--star)" />
    ))}
  </div>
);

const testimonials = [
  { name: "Shine Star", when: "3 months ago", text: "This institute has helped me improve not only my technical knowledge but also my confidence. The trainers encourage students to solve problems on their own while providing guidance whenever required. The course content is relevant, and the practical sessions are very useful. I genuinely enjoyed learning here." },
  { name: "Suraj Agasti", when: "3 months ago", text: "I was looking for a place where I could learn beyond theory, and this institute met my expectations. The practical labs, discussions, and assignments kept me engaged throughout the course. The trainers are approachable and always motivate students to do better." },
  { name: "Saachi Sharma", when: "3 months ago", text: "I joined after hearing positive feedback from others, and my experience has been equally good. The trainers explain every topic clearly and provide practical examples that make learning easier. The classes are interactive, and the institute maintains a professional atmosphere throughout the course." },
  { name: "Sameer", when: "2 months ago", text: "The AI and Machine Learning sessions were well structured and beginner-friendly. I liked that the trainers first explained the fundamentals before introducing more advanced concepts. Learning about prompt engineering alongside AI tools gave me a real edge." },
  { name: "Dheeraj Bisht", when: "2 months ago", text: "What makes this institute different is the opportunity to explore multiple technology domains in one learning environment. Whether someone is interested in networking, cloud, cybersecurity, AI, web development, or data science, there's a path for everyone." },
];

const initials = (name) => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export const TestimonialsSection = ({ onCta }) => (
  <section className="py-24 sm:py-32" style={{ background: "var(--bg-secondary)" }} data-testid="testimonials-section">
    <div className="max-w-6xl mx-auto px-5">
      <SectionHead
        label="Student Voices"
        title={<>What Our <Teal>Students</Teal> Say</>}
        sub="Real feedback from real students who trained with us."
      />
      <div className="mt-14 flex gap-5 overflow-x-auto snap-x snap-mandatory snap-carousel pb-2 lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
        {testimonials.map((t, i) => (
          <Reveal key={t.name} delay={i * 80} className="min-w-[86%] sm:min-w-[55%] lg:min-w-0 snap-start">
            <div className="track-card h-full rounded-2xl p-6 flex flex-col" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`testimonial-card-${i}`}>
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center font-display font-semibold text-sm shrink-0"
                  style={{ background: "rgba(14, 217, 181, 0.12)", border: "1px solid rgba(14, 217, 181, 0.3)", color: "var(--accent)" }}
                >
                  {initials(t.name)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{t.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Stars />
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>{t.when}</span>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed flex-1" style={{ color: "var(--text-body)" }}>"{t.text}"</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={150}>
        <p className="mt-10 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--text-body)" }} data-testid="testimonials-trust-line">
          <GoogleG />
          <Star size={14} style={{ color: "var(--star)" }} fill="var(--star)" />
          <span><strong className="text-white">4.9/5</strong> average rating from 1,000+ students</span>
        </p>
      </Reveal>
      <Reveal delay={250} className="mt-8 text-center">
        <button data-testid="testimonials-cta-button" onClick={onCta} className="cta-button inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold">
          Book My Free Session
          <ArrowRight size={18} />
        </button>
      </Reveal>
    </div>
  </section>
);

const programs = [
  { icon: ShieldCheck, title: "Cyber Security Course", duration: "1 Year", desc: "For students who want to protect companies from online threats." },
  { icon: BrainCircuit, title: "AI & Machine Learning Course", duration: "1 Year", desc: "For students who want to build smart, modern technology." },
  { icon: LineChart, title: "Data Science Course", duration: "1 Year", desc: "For students who enjoy working with numbers and finding patterns." },
  { icon: BarChart3, title: "Data Analytics Course", duration: "6 Months", desc: "A shorter path into a data-focused job." },
  { icon: Cloud, title: "Cloud & Networking Course", duration: "1 Year", desc: "For students interested in how computer systems connect and run." },
];

export const ProgramsSection = () => (
  <section className="py-24 sm:py-32" style={{ background: "var(--bg-primary)" }} data-testid="programs-section">
    <div className="max-w-6xl mx-auto px-5">
      <SectionHead label="Our Programs" title={<>Pick the <Teal>Course</Teal> That Fits You</>} />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {programs.map((p, i) => (
          <Reveal key={p.title} delay={i * 100}>
            <div className="track-card h-full rounded-2xl p-7" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`program-card-${i}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(14, 217, 181, 0.08)", color: "var(--accent)" }}>
                  <p.icon size={20} strokeWidth={1.75} />
                </div>
                <span className="text-xs font-semibold rounded-full px-3 py-1" style={{ color: "var(--accent)", background: "rgba(14, 217, 181, 0.08)" }}>{p.duration}</span>
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white tracking-wide leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-body)" }}>{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={200}>
        <p className="mt-10 text-center text-base md:text-lg" style={{ color: "var(--text-muted)" }} data-testid="programs-caption">
          Not sure which one is right for you? That's what the free session is for.
        </p>
      </Reveal>
    </div>
  </section>
);

const classroomPoints = [
  "Teachers who explain everything clearly",
  "Small groups, so every question gets answered",
  "Hands-on practice, not just reading",
];

export const ClassroomSection = ({ onCta }) => (
  <section className="py-24 sm:py-32" style={{ background: "var(--bg-secondary)" }} data-testid="classroom-section">
    <div className="max-w-6xl mx-auto px-5">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <Reveal className="relative">
          <div className="relative" data-testid="classroom-image-card">
            <div className="absolute -inset-4 rounded-3xl" style={{ background: "rgba(14, 217, 181, 0.06)", filter: "blur(24px)" }} />
            <img
              src={CLASSROOM_IMG}
              alt="A live training session with a teacher guiding students at computers"
              loading="lazy"
              className="relative w-full rounded-2xl object-cover aspect-[4/3]"
              style={{ border: "1px solid var(--input-border)" }}
              data-testid="classroom-image"
            />
            <button
              data-testid="classroom-watch-badge"
              onClick={onCta}
              className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-transform hover:scale-105"
              style={{ background: "rgba(13, 17, 23, 0.85)", backdropFilter: "blur(8px)", border: "1px solid var(--input-border)", color: "white" }}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "var(--accent)", color: "#0D1117" }}>
                <Play size={11} fill="currentColor" />
              </span>
              Watch a Session
            </button>
          </div>
        </Reveal>
        <Reveal delay={150}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase" style={{ color: "var(--accent)" }}>
            <span className="w-5 h-px" style={{ background: "var(--accent)" }} />
            See for Yourself
          </span>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold uppercase text-white leading-[1.12]" data-testid="classroom-headline">
            What a <Teal>Real Class</Teal> Looks Like
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-body)" }} data-testid="classroom-text">
            This is what learning here actually feels like — a real teacher, real explanations, students asking
            questions and getting answers right away. No confusion, just clear learning.
          </p>
          <ul className="mt-7 space-y-3.5" data-testid="classroom-checklist">
            {classroomPoints.map((p) => (
              <li key={p} className="flex items-center gap-3 text-base" style={{ color: "var(--text-body)" }}>
                <Check size={18} style={{ color: "var(--accent)" }} strokeWidth={3} className="shrink-0" />
                {p}
              </li>
            ))}
          </ul>
          <button
            data-testid="classroom-cta-button"
            onClick={onCta}
            className="cta-button mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold"
          >
            Book My Free Session
            <ArrowRight size={18} />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const VisitSection = () => (
  <section className="py-24 sm:py-32" style={{ background: "var(--bg-primary)" }} data-testid="visit-section">
    <div className="max-w-6xl mx-auto px-5">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase" style={{ color: "var(--accent)" }}>
            <span className="w-5 h-px" style={{ background: "var(--accent)" }} />
            Visit Us
          </span>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold uppercase text-white leading-[1.12]" data-testid="visit-headline">
            Come Meet Us <Teal>In Person</Teal>
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-body)" }} data-testid="visit-subtext">
            Real classrooms. Real teachers. Right here in your city.
          </p>
          <div className="mt-7 space-y-3.5" data-testid="visit-locations">
            <p className="flex items-center gap-3 text-base" style={{ color: "var(--text-body)" }}>
              <MapPin size={18} className="shrink-0" style={{ color: "var(--accent)" }} />
              Preet Vihar, New Delhi
            </p>
          </div>
          <img
            src="/images/classroom-2.jpg"
            alt="Our institute's front desk and reception area"
            loading="lazy"
            className="mt-8 w-full rounded-2xl object-cover aspect-[16/10]"
            style={{ border: "1px solid var(--input-border)" }}
            data-testid="visit-photo"
          />
        </Reveal>
        <Reveal delay={150} className="relative">
          <div
            className="relative rounded-2xl overflow-hidden h-[220px] sm:h-[340px]"
            style={{ border: "1px solid var(--input-border)", background: "var(--bg-card)" }}
            data-testid="visit-map-card"
          >
            <iframe
              title="Institute location map"
              src={MAP_EMBED_SRC}
              className="w-full h-full"
              style={{ border: 0, filter: "grayscale(0.45) brightness(0.82) contrast(1.05)" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="visit-map"
            />
            <span
              className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold"
              style={{ background: "rgba(13, 17, 23, 0.85)", backdropFilter: "blur(8px)", border: "1px solid var(--input-border)", color: "var(--accent)" }}
              data-testid="visit-map-badge"
            >
              <MapPin size={13} />
              We're Here
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
